import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    List,
    Row,
    Tooltip
} from "antd";
import { useEffect, useRef, useState } from "react";
import "./eventDetails.css";

import { DataCard, GenericSearchableList, useResponsiveSettings, util } from "@seims/shared";

export const DataCapturerCard = ({
  exportAdmins = () => {},
  existingAdmins = [],
  allowedToSave = false,
  onSaveAdminBE,
}) => {

  const [adminForm] = Form.useForm();
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingAdminIndex, setEditingAdminIndex] = useState(null);

  const [admins, setAdmins] = useState(existingAdmins || []);
  const [adminsToDisplay, setAdminsToDisplay] = useState(existingAdmins || []);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const hasInitialized = useRef(existingAdmins?.length > 0);
  
  const { pageSize } = useResponsiveSettings(); 

  const handleSearch = (text) => {
    setCurrentPage(1);

    const filteredAdmins = admins.filter(
      (admin) =>
        admin.name?.toLowerCase().includes(text.toLowerCase()) ||
        admin.surname?.toLowerCase().includes(text.toLowerCase()) ||
        admin.email?.toLowerCase().includes(text.toLowerCase()),
    );

    setAdminsToDisplay(filteredAdmins);
  };

  const handleDeleteAdmin = (adminToDelete) => {
    const adminsRemaining = admins.filter((admin) => admin !== adminToDelete);
    setAdmins(adminsRemaining);
  };

  const handleEditAdmin = (adminToEdit) => {
    const adminIndex = admins.indexOf(adminToEdit);
    if (adminIndex === -1) return;
    adminForm.setFieldsValue({
      name: adminToEdit.name,
      surname: adminToEdit.surname,
      email: adminToEdit.email,
      confirmEmail: adminToEdit.email,
    });
    setEditingAdminIndex(adminIndex);
    setShowAdminForm(true);
  };

  const handleFormFinish = async (values) => {

    try {
      // 1. If BE save is allowed and the function is provided, call it first!
      if (allowedToSave && onSaveAdminBE) {

        setIsSaving(true);
        // Pass the values, and optionally the index if you need to know if it's an edit
        await onSaveAdminBE(values, editingAdminIndex);
      }

      // 2. If the BE call succeeds (or if allowedToSave is false), update local UI state
      if (editingAdminIndex !== null) {
        // Edit existing admin
        setAdmins((prev) =>
          prev.map((a, i) => (i === editingAdminIndex ? values : a)),
        );
      } else {
        // Add new admin
        setAdmins((prev) => [...prev, values]);
      }

      // 3. Clean up and close
      adminForm.resetFields();
      setEditingAdminIndex(null);
      setShowAdminForm(false);
    } catch (error) {
      // The BE call failed. Do not close the form or update the local list.
      console.error("Failed to save admin to backend", error);
      // Optional: Add AntD notification here
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (existingAdmins?.length > 0 && !hasInitialized.current) {
      setAdmins(existingAdmins);
      hasInitialized.current = true;
    }

    setAdminsToDisplay(existingAdmins);
  }, [existingAdmins]);

  useEffect(() => {
    if (exportAdmins) {
      exportAdmins(admins);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admins]);

  const onNameBlur = (e) => {
        const { value, name } = e.target;
        const textValue = util.toPascalText(value);
        adminForm.setFieldsValue({ [name]: textValue });
      }

  const renderAdminInlineForm = () => (
    <Card
      variant="borderless"
      className="event-card-borderless"
      classNames={{
        header: "event-card-borderless-header",
        body: "event-card-borderless-body",
      }}
    >
      <Form
        form={adminForm}
        layout="vertical"
        onFinish={handleFormFinish}
        component={false}
      >
      
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="id"
              hidden
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="name"
              rules={[{ required: true }]}
            >
              <Input name="name" onBlur={onNameBlur} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Surname"
              name="surname"
              rules={[{ required: true }]}
            >
              <Input name="surname" onBlur={onNameBlur} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input type="email" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Email Address"
              name="confirmEmail"
              dependencies={["email"]}
              rules={[
                { required: true, message: "Please confirm email address" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("email") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Email addresses do not match"),
                    );
                  },
                }),
              ]}
              
            >
              <Input type="email" />
            </Form.Item>
          </Col>
        </Row>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end"  }}>
          <Button
            type="default"
            disabled={isSaving} // Prevent canceling while saving
            onClick={() => {
              adminForm.resetFields();
              setEditingAdminIndex(null);
              setShowAdminForm(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isSaving} // Shows AntD spinner during BE call
            onClick={(e) => {
              e.preventDefault(); // Stops default browser behavior
              e.stopPropagation(); // Stops the click from bubbling to the parent form
              adminForm.submit(); // Programmatically triggers this specific form's onFinish
            }}
          >
            Save
          </Button>
        </div>
      </Form>
    </Card>
  );

  return (
    <Card
      variant="borderless"
      className="event-card-borderless"
      classNames={{
        header: "event-card-borderless-header",
        body: "event-card-borderless-body",
      }}
      title={
        <div className="event-card-borderless-title-container">
          <span className="event-card-borderless-header-text">
            Event Data Capturer
          </span>
          
        </div>
      }
    >
      {/* Inline Form */}

      <Button
            type="primary"
            className="add-capturer"
            shape="round"
            onClick={() => {
              setEditingAdminIndex(null);
              adminForm.resetFields();
              setShowAdminForm(true);
            }}
          >
            + Add Data Capturer +
          </Button>
          <br/>
      {showAdminForm && renderAdminInlineForm()}

      <GenericSearchableList
        searchHint="Search for a data capturer"
        onHandleSearch={handleSearch}
        dataSource={adminsToDisplay}
        pagination={{
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
        }}
        renderItem={(admin, index) => {
          const rowIndex = (currentPage - 1) * pageSize + index;

          // Map the admin data to the generic rows
          const cardData = [
            { label: "Name", value: `${admin.name} ${admin.surname}` },
            { label: "Email", value: admin.email },
          ];

          return (
            <List.Item>
              <DataCard
                onToggleSelect={() => {}} // No selection needed, but DataCard expects this prop
                index={rowIndex}
                isSelected={true}
                details={cardData}
                actions={allowedToSave ? [] : [ // No editing or deleting allowed on backend bound screen
                <Tooltip
                  title="Delete Data Capturer"
                  key="delete-data-capturer-tooltip"
                  placement="top"
                  trigger={["hover", "click"]}
                >
                  <Button
                        tooltip="Delete Data Capturer"
                        key="delete-data-capturer"
                        type="text"
                        className="icon-circle-btn"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation(); // Good practice even if card isn't clickable
                          handleDeleteAdmin(admin);
                        }}
                      />
                  </Tooltip>
                  ,
                  <Tooltip
                  title="Edit Data Capturer"
                  key="edit-data-capturer-tooltip"
                  placement="top"
                  trigger={["hover", "click"]}
                >
                  <Button
                    key="edit-data-capturer"
                    type="text"
                    className="icon-circle-btn"
                    style={{ color: "#1677ff" }} // Primary blue color
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAdmin(admin);
                    }}
                  />
                </Tooltip>
                  ,
                ]}
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
};
